import { mlvApplyZ } from './mlvZLayers.js';
import { mlvSaveFocus } from './mlvFocusRestore.js';
import { mlvModalHistoryAttach } from '../navigation/mlvModalHistoryBridge.js';
import { mlvTrack } from '../telemetry/mlvTrack.js';

/**
 * Modal ligero para invitados: invita a iniciar sesión con Google (sin re-renderizar la pantalla).
 * Acepta `onLogin` y/o `onRegister` (ambos disparan el mismo flujo de login con Google).
 * @param {{ onLogin?: () => void, onRegister?: () => void }} opts
 */
export function openGuestAuthModal({ onLogin, onRegister }) {
  const onIngresar = onLogin || onRegister;
  document.getElementById('mlv-guest-auth-modal')?.remove();

  const restoreFocus = mlvSaveFocus();

  const backdrop = document.createElement('div');
  backdrop.id = 'mlv-guest-auth-modal';
  backdrop.className =
    'fixed inset-0 flex items-end justify-center sm:items-center p-4 bg-black/45 backdrop-blur-[2px]';
  mlvApplyZ(backdrop, 'guestAuth');
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-labelledby', 'mlv-guest-auth-title');

  backdrop.innerHTML = `
    <div class="mlv-guest-auth-panel w-full max-w-md rounded-t-3xl border border-slate-200/90 bg-white p-5 shadow-2xl sm:rounded-3xl">
      <div class="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 id="mlv-guest-auth-title" class="text-base font-bold text-slate-900">Estás viendo el barrio como invitado</h2>
          <p class="mt-1.5 text-sm leading-relaxed text-slate-600">Para <strong class="font-semibold text-slate-800">publicar</strong>, usar tu <strong class="font-semibold text-slate-800">perfil</strong> o guardar favoritos, ingresá con tu cuenta de Google. Es gratis y rápido.</p>
        </div>
        <button type="button" class="mlv-guest-auth-close shrink-0 rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800" aria-label="Cerrar">
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <button type="button" id="mlv-guest-auth-login" class="w-full rounded-xl bg-brand-primary py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2">
        Ingresar con Google
      </button>
    </div>
  `;

  const hist = mlvModalHistoryAttach(() => {
    document.removeEventListener('keydown', onKey);
    try {
      restoreFocus();
    } catch (_e) {
      /* ignore */
    }
    backdrop.remove();
  });

  function remove() {
    hist.closeFromUi();
  }

  function onKey(e) {
    if (e.key === 'Escape') remove();
  }

  document.body.appendChild(backdrop);
  document.addEventListener('keydown', onKey);

  /** Solo cerrar al tocar el fondo oscuro, no al soltar el dedo "a través" del panel (evita taps fantasmas en móvil). */
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) remove();
  });

  function trackAndWire(btn, eventName, fn) {
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      mlvTrack(eventName, {});
      btn.disabled = true;
      remove();
      queueMicrotask(() => fn?.());
    });
  }

  backdrop.querySelector('.mlv-guest-auth-close')?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    mlvTrack('guest_auth_dismiss', {});
    remove();
  });
  trackAndWire(backdrop.querySelector('#mlv-guest-auth-login'), 'guest_auth_login', onIngresar);

  requestAnimationFrame(() => {
    backdrop.querySelector('#mlv-guest-auth-login')?.focus();
  });
}
